import { Suspense, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Text } from '@react-three/drei'
import * as THREE from 'three'

function Shield() {
  const groupRef = useRef<THREE.Group>(null)
  const glowRef = useRef<THREE.PointLight>(null)

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.15 + 0.2
      groupRef.current.rotation.x = Math.cos(state.clock.getElapsedTime() * 0.2) * 0.05
    }
    if (glowRef.current) {
      glowRef.current.intensity = 0.3 + Math.sin(state.clock.getElapsedTime() * 2) * 0.15
    }
  })

  return (
    <group ref={groupRef} scale={2.0}>
      {/* Main shield body */}
      <mesh>
        <cylinderGeometry args={[1.2, 1.4, 0.15, 6]} />
        <meshStandardMaterial
          color="#1a1a24"
          metalness={0.95}
          roughness={0.1}
          envMapIntensity={2}
        />
      </mesh>

      {/* Inner shield face */}
      <mesh position={[0, 0, 0.08]}>
        <cylinderGeometry args={[1.05, 1.25, 0.1, 6]} />
        <meshStandardMaterial
          color="#0f0f14"
          metalness={0.9}
          roughness={0.15}
        />
      </mesh>

      {/* OKX Text */}
      <Text
        position={[0, 0, 0.15]}
        fontSize={0.35}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        fontWeight={700}
      >
        OKX
      </Text>

      {/* Point light for glow effect */}
      <pointLight ref={glowRef} position={[0, 0, 1]} color="#ff00ff" intensity={0.3} distance={5} />

      {/* Ambient lights */}
      <pointLight position={[2, 2, 2]} color="#ff00ff" intensity={0.4} distance={10} />
      <pointLight position={[-2, -1, 2]} color="#00ffff" intensity={0.3} distance={10} />
    </group>
  )
}

function FloatingOrbs() {
  const orbRefs = useRef<THREE.Mesh[]>([])

  useFrame((state) => {
    orbRefs.current.forEach((orb, i) => {
      if (orb) {
        const offset = i * 1.5
        orb.position.y = Math.sin(state.clock.getElapsedTime() * 0.5 + offset) * 0.3
        orb.position.x = Math.cos(state.clock.getElapsedTime() * 0.3 + offset) * 0.2
      }
    })
  })

  const orbs = [
    { color: '#ff00ff', scale: 0.08, position: [2, 1, 0] as [number, number, number] },
    { color: '#00ffff', scale: 0.06, position: [-1.8, 0.5, 0.5] as [number, number, number] },
    { color: '#ff00ff', scale: 0.05, position: [1.5, -1, 0.3] as [number, number, number] },
    { color: '#00ffff', scale: 0.07, position: [-1.5, -0.5, -0.5] as [number, number, number] },
    { color: '#ff00ff', scale: 0.04, position: [0.5, 1.5, -0.3] as [number, number, number] },
  ]

  return (
    <>
      {orbs.map((orb, i) => (
        <mesh
          key={i}
          ref={(el) => { if (el) orbRefs.current[i] = el }}
          position={orb.position}
        >
          <sphereGeometry args={[orb.scale, 16, 16]} />
          <meshBasicMaterial color={orb.color} transparent opacity={0.7} />
        </mesh>
      ))}
    </>
  )
}

function CyberOrb() {
  const groupRef = useRef<THREE.Group>(null)
  const wireframeRef = useRef<THREE.Mesh>(null)
  const glowRef = useRef<THREE.Mesh>(null)

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.getElapsedTime() * 0.15
      groupRef.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.1) * 0.2
    }
    if (wireframeRef.current) {
      wireframeRef.current.rotation.y = -state.clock.getElapsedTime() * 0.3
      wireframeRef.current.rotation.z = state.clock.getElapsedTime() * 0.1
    }
    if (glowRef.current) {
      const scale = 1.1 + Math.sin(state.clock.getElapsedTime() * 2) * 0.05
      glowRef.current.scale.set(scale, scale, scale)
    }
  })

  return (
    <group ref={groupRef} position={[0, 0, -0.5]}>
      {/* Core sphere */}
      <mesh>
        <sphereGeometry args={[1.5, 32, 32]} />
        <meshStandardMaterial
          color="#0a0a14"
          metalness={0.9}
          roughness={0.2}
          envMapIntensity={1.5}
        />
      </mesh>

      {/* Wireframe overlay */}
      <mesh ref={wireframeRef}>
        <icosahedronGeometry args={[1.6, 1]} />
        <meshBasicMaterial
          color="#ff00ff"
          wireframe
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* Outer glow sphere */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[1.8, 24, 24]} />
        <meshBasicMaterial
          color="#00ffff"
          transparent
          opacity={0.08}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Floating particles */}
      <Particles />
    </group>
  )
}

function Particles() {
  const pointsRef = useRef<THREE.Points>(null)

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.getElapsedTime() * 0.05
      pointsRef.current.rotation.x = state.clock.getElapsedTime() * 0.03
    }
  })

  const count = 150
  const positions = new Float32Array(count * 3)
  const colors = new Float32Array(count * 3)

  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2
    const phi = Math.acos(2 * Math.random() - 1)
    const r = 2.0 + Math.random() * 1.5
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
    positions[i * 3 + 2] = r * Math.cos(phi)

    const isPink = Math.random() > 0.5
    colors[i * 3] = isPink ? 1 : 0
    colors[i * 3 + 1] = isPink ? 0 : 1
    colors[i * 3 + 2] = 1
  }

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.05} vertexColors transparent opacity={0.9} sizeAttenuation />
    </points>
  )
}

function Scene() {
  return (
    <>
      <ambientLight intensity={0.2} />
      <directionalLight position={[5, 5, 5]} intensity={0.5} />
      <directionalLight position={[-3, 3, 2]} intensity={0.3} color="#00ffff" />
      <Shield />
      <FloatingOrbs />
      <CyberOrb />
    </>
  )
}

function LogoFallback() {
  return (
    <div className="w-64 h-64 rounded-full bg-gradient-to-br from-[#1a1a24] to-[#0a0a0f] border border-[rgba(255,0,255,0.2)] flex items-center justify-center">
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#ff00ff] to-[#00ffff] opacity-50 animate-pulse" />
    </div>
  )
}

export default function LogoScene() {
  return (
    <Suspense fallback={<LogoFallback />}>
      <Canvas
        camera={{ position: [0, 0, 6], fov: 50 }}
        style={{ background: 'transparent' }}
        gl={{ antialias: true, alpha: true }}
      >
        <Scene />
      </Canvas>
    </Suspense>
  )
}
